/**
 * Lifestyle quiz — Figma Quizzes › `Lifestyle quiz · desktop` 3229:105744 / `mobile`
 * 3229:106519. Welcome → Occupation (Other opens a text field, R171) → Dress code
 * (skipped for At home and Study) → Week (multi) → Events and trips → Climate → Note →
 * Result. Every question is optional and applied at once (R203); no AI mark.
 *
 * The questions, their options, labels and the `show_if` rules come from the published
 * `default-lifestyle-survey` definition (v2, BE-152) — nothing is hard-coded here; only
 * the chrome (welcome, result, leave) is the app's own copy.
 */
import {
  localizedText,
  type SurveyResponseAnswer,
  type SurveyResponseBody,
  type SurveyV2Config,
  type SurveyV2Question,
  type UserLifestyle,
} from '@/features/survey/api/surveys'

export type LifestyleQuizQuestionId = string

export interface LifestyleQuizOption {
  value: string
  label: string
  icon?: string
}

export interface LifestyleQuizQuestion {
  id: LifestyleQuizQuestionId
  multi: boolean
  title: string
  subtitle: string
  options: readonly LifestyleQuizOption[]
  /** The option that opens a text field (R171) — the definition's `allow_custom`. */
  customValue?: string
  /** Placeholder of the custom text field. */
  customPlaceholder: string
  maxLength: number
  /** Asked only when the named earlier question has one of these keys chosen. */
  showIf: { questionId: string; optionKeys: string[] } | null
}

/** The free-text closing question (`type: text`). */
export interface LifestyleQuizNote {
  id: string
  title: string
  subtitle: string
  placeholder: string
  maxLength: number
}

export interface LifestyleQuizDefinition {
  questions: LifestyleQuizQuestion[]
  note: LifestyleQuizNote | null
}

/** The key the client sends for the free-text "Other" answer of an `allow_custom` question. */
export const LIFESTYLE_QUIZ_CUSTOM_KEY = 'other'
export const LIFESTYLE_QUIZ_NOTE_MAX = 2000

function stepShowIf(question: SurveyV2Question, inherited: SurveyV2Question['showIf']) {
  const rule = question.showIf ?? inherited ?? null
  return rule ? { questionId: rule.questionId, optionKeys: [...rule.optionKeys] } : null
}

/** The definition → the quiz's questions and note, in the app's language. */
export function lifestyleQuizDefinitionFrom(
  config: SurveyV2Config | null | undefined,
  lang: string,
): LifestyleQuizDefinition {
  const questions: LifestyleQuizQuestion[] = []
  let note: LifestyleQuizNote | null = null
  for (const step of config?.steps ?? []) {
    for (const question of step.questions ?? []) {
      if (question.type === 'text') {
        note ??= {
          id: question.id,
          title: localizedText(question.title, lang),
          subtitle: localizedText(question.subtitle, lang),
          placeholder: localizedText(question.placeholder, lang),
          maxLength: question.maxLength ?? LIFESTYLE_QUIZ_NOTE_MAX,
        }
        continue
      }
      if (question.type !== 'single' && question.type !== 'multi') continue
      const options: LifestyleQuizOption[] = (question.options ?? []).map((option) => ({
        value: option.key,
        label: localizedText(option.label, lang),
        ...(option.icon ? { icon: option.icon } : {}),
      }))
      const custom = question.allowCustom ? options.find((option) => option.value === LIFESTYLE_QUIZ_CUSTOM_KEY) : null
      questions.push({
        id: question.id,
        multi: question.type === 'multi',
        title: localizedText(question.title, lang),
        subtitle: localizedText(question.subtitle, lang),
        options,
        ...(custom ? { customValue: custom.value } : {}),
        customPlaceholder: localizedText(question.placeholder, lang) || localizedText(question.customLabel, lang),
        maxLength: question.maxLength ?? LIFESTYLE_QUIZ_NOTE_MAX,
        showIf: stepShowIf(question, step.showIf),
      })
    }
  }
  return { questions, note }
}

export type LifestyleQuizStep = 'welcome' | LifestyleQuizQuestionId | 'note' | 'result'

export interface LifestyleQuizState {
  /** single: [value]; multi: values; nothing chosen: [] */
  answers: Partial<Record<LifestyleQuizQuestionId, string[]>>
  /** Text of Other (the `allow_custom` question). */
  custom: string
  note: string
}

export function emptyLifestyleQuizState(): LifestyleQuizState {
  return { answers: {}, custom: '', note: '' }
}

function isShown(question: LifestyleQuizQuestion, state: LifestyleQuizState): boolean {
  if (!question.showIf) return true
  const chosen = state.answers[question.showIf.questionId] ?? []
  // Not answered yet — the rule cannot say no (the question is asked, skip is free).
  if (!chosen.length) return true
  return chosen.some((value) => question.showIf!.optionKeys.includes(value))
}

/** The definition's `show_if` rules decide the steps (Dress code goes for At home / Study). */
export function lifestyleQuizSteps(questions: readonly LifestyleQuizQuestion[], state: LifestyleQuizState): LifestyleQuizStep[] {
  const steps: LifestyleQuizStep[] = ['welcome']
  for (const question of questions) {
    if (isShown(question, state)) steps.push(question.id)
  }
  steps.push('note', 'result')
  return steps
}

/** Single choice replaces (tapping the chosen one clears it); multi toggles. */
export function toggleLifestyleQuizAnswer(current: string[] | undefined, value: string, multi: boolean): string[] {
  const chosen = current ?? []
  if (multi) return chosen.includes(value) ? chosen.filter((item) => item !== value) : [...chosen, value]
  return chosen[0] === value ? [] : [value]
}

export function lifestyleQuizProgress(steps: LifestyleQuizStep[], step: LifestyleQuizStep): number {
  const index = steps.indexOf(step)
  if (index <= 0) return 0
  return Math.min(1, index / (steps.length - 1))
}

/**
 * `PUT /surveys/{key}/response` — the canonical shapes: `{option_keys}` for a choice,
 * `{key: "other", text}` for the typed Other, `{custom_text}` for the note.
 */
export function lifestyleQuizResponseBody(
  definition: LifestyleQuizDefinition,
  state: LifestyleQuizState,
  steps: LifestyleQuizStep[],
): SurveyResponseBody {
  const answers: Record<string, SurveyResponseAnswer> = {}
  for (const question of definition.questions) {
    if (!steps.includes(question.id)) continue
    const chosen = state.answers[question.id] ?? []
    if (!chosen.length) continue
    const custom = question.customValue && chosen[0] === question.customValue ? state.custom.trim() : ''
    answers[question.id] = custom
      ? { key: 'other', text: custom.slice(0, question.maxLength) }
      : { optionKeys: question.multi ? [...chosen] : [chosen[0]!] }
  }
  const noteMax = definition.note?.maxLength ?? LIFESTYLE_QUIZ_NOTE_MAX
  const note = state.note.trim().slice(0, noteMax)
  if (definition.note && note) answers[definition.note.id] = { customText: note }
  return { answers, freeText: note || null }
}

/** What the server stored, as the quiz's state (questions it does not know are ignored). */
export function lifestyleQuizStateFrom(
  definition: LifestyleQuizDefinition,
  lifestyle: UserLifestyle | null | undefined,
): LifestyleQuizState {
  const state = emptyLifestyleQuizState()
  const known = new Map(definition.questions.map((question) => [question.id, question]))
  for (const answer of lifestyle?.answers ?? []) {
    const question = known.get(answer.questionId)
    if (!question) continue
    const keys = (answer.optionKeys?.length ? answer.optionKeys : [answer.answerKey]).filter(Boolean)
    const custom = answer.customText?.trim() ?? ''
    if (question.customValue && (keys.includes(question.customValue) || (custom && !keys.length))) {
      state.answers[question.id] = [question.customValue]
      state.custom = custom
      continue
    }
    const valid = keys.filter((key) => question.options.some((option) => option.value === key))
    if (!valid.length) continue
    state.answers[question.id] = question.multi ? valid : [valid[0]!]
  }
  state.note = lifestyle?.note ?? ''
  return state
}

/**
 * After a transport / server failure of the PUT (not a 422): an inner step may move on — the
 * answers stay in the layer and the next step's PUT retries them (R203). The step that leads to
 * the Result screen may NOT: Result would present answers that were never saved, and nothing
 * retries after it (audit 2026-10-19 #6).
 */
export function lifestyleQuizMayMoveOnAfterFailedSave(next: LifestyleQuizStep | undefined): boolean {
  return next !== 'result'
}
